import { getCookie } from 'tiny-cookie';
import { TrackJS } from 'trackjs';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { environment } from '@/helpers/Environment';
import EventBus from '@/helpers/EventBus';
import { fullPathMinusHash } from '@/helpers/route';
import TheFooter from '@/components/Footer.vue';
import TheHeader from '@/components/Header.vue';
import MobileCartWidget from '@/components/cart/MobileCartWidget.vue';
import { apiErrorMessage, fallbackErrorMessage, logInTrackJS } from '@/errors/helpers';
import { closeCartModal } from '@/modals/cartModal';
import ModalHandler from '@/modals/helpers/ModalHandler.vue';
import { registerModalEventListeners } from '@/modals/helpers/api';
import CartExpire from '@/plugins/CartExpire';
import { isLoggedIn } from '@/state/Authentication';
import store from '@/store/store';
@Component({
    name: 'App',
    mixins: [CartExpire],
    components: {
        ModalHandler,
        TheFooter,
        TheHeader,
        MobileCartWidget,
    },
})
export default class default_1 extends Vue {
    error = null;
    cartAddedAnnouncement = null;
    created() {
        EventBus.$on('cart:items-added', this.announceCartItemsAdded);
        window.addEventListener('unhandledrejection', (error) => {
            const apiError = apiErrorMessage(error.reason) ?? null;
            if (apiError) {
                this.error = apiError;
                logInTrackJS(`Unhandled Tix API rejection: ${this.error}`, error.reason);
            }
            else {
                this.tellUserAboutUnexpectedError();
            }
            // Do not throw the error. That would cause it to be duplicated.
        });
        // Register the generic modals event listener.
        registerModalEventListeners();
        // Regularly check if the logged in user's session has expired by
        // looking at the login time and how long a session is valid for.
        TrackJS.addMetadata('Authenticated on init', getCookie('session_expires') ? 'yes' : 'no');
        setInterval(() => {
            // WARNING: Errors here may cause the server to get hammered with continuous
            // queries from all browsers open on all devices.
            const hasSessionCookie = getCookie('session_expires');
            TrackJS.addMetadata('Authenticated', hasSessionCookie ? 'yes' : 'no');
            if (isLoggedIn() && !hasSessionCookie) {
                TrackJS.addMetadata('Was previously authenticated', 'yes');
                // Session cookie already disappeared. Force logout and redirect to homepage.
                // initializeStores() also handles this scenario on initialization and without redirecting.
                this.endSession();
            }
        }, 1000);
        window.dataLayer.push({
            // Required a data layer variable set up on GTM
            tenant_slug: environment.portal.slug,
        });
        // Place screen readers at top of page on reroute
        this.$router.afterEach((to, from) => {
            // Clear the announcement when navigating to a new route.
            this.cartAddedAnnouncement = null;
            // If hash present in url or route did not change, do not reset focus
            if (to.hash || to.path === from.path) {
                return;
            }
            // Need to focus `body-inner` rather than `document.body` as that causes NVDA on Firefox to
            // incorrectly focus the footer.
            const el = document.getElementsByClassName('body-inner')[0];
            el?.focus();
        });
    }
    errorCaptured() {
        this.tellUserAboutUnexpectedError();
    }
    tellUserAboutUnexpectedError() {
        // Tell users that something unexpected went wrong.
        this.error = fallbackErrorMessage;
    }
    clearState() {
        return store.dispatch('clearAll');
    }
    endSession() {
        this.$modals.close({ name: 'membership-modal' });
        // Closes the member menu if opened.
        const menu = document.querySelector('.member-menu');
        if (menu) {
            menu.removeAttribute('open');
        }
        this.clearState();
        this.$router.push('/');
    }
    reload() {
        window.location.reload();
    }
    get routeName() {
        const name = (this.$route.name ?? '').replace('/', '-');
        return `route-${name}`;
    }
    get ticketCount() {
        return store.getters['Cart/ticketCount'];
    }
    get currentRoute() {
        return fullPathMinusHash(this.$route.fullPath);
    }
    @Watch('ticketCount')
    handleTicketCountChange(value) {
        if (value === 0) {
            closeCartModal();
        }
    }
    focusMainContent() {
        // Manually focus and scroll the main content into view
        // We can't use vue-router's hash # URL behaviour because it doesn't focus the target element, which is important for screen readers
        // We can't use an anchor tag with hash # because it will remove the seller slug
        const container = document.querySelector('#main-content');
        container?.focus();
        container?.scrollIntoView();
    }
    announceCartItemsAdded(count) {
        // Make sure the announcement message is changed a moment (~ 1s) after
        // the previous message is cleared in route change.
        setTimeout(() => {
            this.cartAddedAnnouncement = this.$tc('cartWidget.cartAddAnnouncement', count, { count });
        }, 1000);
    }
}
